import { Box } from '@mui/material'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ScheduleDataVersionsPanel } from './ScheduleDataVersions.view'
import { ScheduleSettingsSnapshotView } from './ScheduleSettingsSnapshot.view'
import { SchedulerCommentsPanel } from './SchedulerComments.view'
import {
  useCreateComment,
  useDeleteComment,
  useSchedulerComments,
  useUpdateComment,
} from './Scheduler.data'
import { useCurrentUserEmail } from './ScheduleComments.anchor'

/**
 * Containers for the two sidebar sections introduced by the redesign
 * (Data & Snapshots, Comments). They reuse the existing panels + hooks that
 * previously lived inside ScheduleControlPanel's tabs, so behaviour is
 * unchanged — only the navigation home moves to the sidebar.
 */

export const DataSnapshotsSection = ({
  schedulerId,
  iteration,
  selectedWeek,
}: {
  schedulerId: string
  iteration: string
  selectedWeek: number
}) => {
  const navigate = useNavigate()
  const handleVersionChange = useCallback(
    (versionKey: string) =>
      navigate(`/plan-scheduler/input/${schedulerId}/${versionKey}`),
    [navigate, schedulerId]
  )
  return (
    <Box>
      <ScheduleDataVersionsPanel
        schedulerId={schedulerId}
        iteration={iteration}
        selectedWeek={selectedWeek}
        dataSummary={[]}
        onVersionChange={handleVersionChange}
      />
      <Box sx={{ mt: 2 }}>
        <ScheduleSettingsSnapshotView
          schedulerId={schedulerId}
          iteration={iteration}
        />
      </Box>
    </Box>
  )
}

export const CommentsSection = ({
  schedulerId,
  iteration,
  selectedWeek,
}: {
  schedulerId: string
  iteration: string
  selectedWeek: number
}) => {
  const {
    data: comments = [],
    refetch: refetchComments,
    isLoading: isCommentsLoading,
  } = useSchedulerComments(schedulerId, iteration, selectedWeek)

  const { mutate: updateComment } = useUpdateComment(
    () => {},
    () => toast.error('Failed to update comment')
  )
  const { mutate: deleteComment } = useDeleteComment(
    () => toast.success('Comment deleted'),
    () => toast.error('Failed to delete comment')
  )
  // create kept available for parity with the control-panel flow.
  useCreateComment(
    () => toast.success('Comment added'),
    () => toast.error('Failed to add comment')
  )

  // From MSAL, not localStorage — nothing in the Portal writes a `userEmail`
  // key, so the read this replaces always returned '' and the delete
  // affordance never appeared on the reader's own comments.
  const currentUserEmail = useCurrentUserEmail()

  return (
    <SchedulerCommentsPanel
      comments={comments}
      currentUserEmail={currentUserEmail}
      onRefresh={refetchComments}
      onResolve={(commentId, resolved) =>
        updateComment({ schedulerId, iteration, commentId, data: { resolved } })
      }
      onDelete={(commentId) =>
        deleteComment({ schedulerId, iteration, commentId })
      }
      onCommentClick={() => {}}
      isLoading={isCommentsLoading}
    />
  )
}
